import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {ChallengeBuilderProject, ChallengeMetric, ChallengeTarget} from 'src/app/core/model/project';
import {
  ChallengeTargetsAction,
  SetBackLoadingAction,
  SetNextLoadingAction,
  UpdateChallengeNextValidAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {MessageService} from 'src/app/services/message.service';
import {DurationDisplayPipe} from 'src/app/shared/pipes/duration-display.pipe';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {durationDisplayToSeconds, secondsToDurationDisplay} from 'src/app/shared/utils/duration.util';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {CbTaskIconButtonComponent} from '../shared/cb-task-icon-button/cb-task-icon-button.component';

interface OperatorOption {
  display: string;
  displayValue: string;
  value: string;
}

interface PeriodOption {
  display: string;
  value: string;
}

type DurationType = 'hrs' | 'hrs/min' | 'min' | 'min/sec' | 'sec';

// Map display values to actual duration type values
const DURATION_TYPE_MAP: Record<string, DurationType> = {
  hrs: 'hrs',
  'hrs/min': 'hrs/min',
  'hrs : min': 'hrs/min',
  min: 'min',
  'min/sec': 'min/sec',
  'min : sec': 'min/sec',
  sec: 'sec',
};

@Component({
  selector: 'cb-targets',
  standalone: true,
  imports: [CommonModule, MatMenuModule, MatIconModule, DurationDisplayPipe, CbTaskIconButtonComponent],
  templateUrl: './cb-targets.component.html',
  styleUrl: './cb-targets.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbTargetsComponent implements OnInit {
  private nextClickSubscription?: Subscription;
  private backClickSubscription?: Subscription;
  private readonly store$ = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly tasksService = inject(TasksService);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;

  nextClick$ = input.required<Observable<void>>();
  backClick$ = input.required<Observable<void>>();
  cbProject = input<ChallengeBuilderProject | undefined>();

  onNext = output<void>();
  onBack = output<void>();

  TrackerMetricTypes = TrackerMetricTypes;

  // Signal for local task groups (tasks with their metrics containing targets)
  localTaskGroups = signal<{taskName: string; taskId: string; metrics: ChallengeMetric[]}[]>([]);

  // Track task icons by taskId
  taskIcons = signal<Record<string, string>>({});

  // Track whether we've initialized (plain boolean to avoid being a signal dependency)
  private initialized = false;

  // Effect to initialize task groups when project data becomes available
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly projectEffect = effect(
    () => {
      const cbProject = this.cbProject();
      if (cbProject?.challengeBuilder?.objectives && !this.initialized) {
        this.initialized = true;
        const groups = this.extractTaskGroups(cbProject);
        this.localTaskGroups.set(groups);
        this.initTaskIcons(cbProject);
      }
    },
    {allowSignalWrites: true}
  );

  // Extract task groups from objectives with metrics (targets are nested in metrics)
  private extractTaskGroups(
    cbProject: ChallengeBuilderProject
  ): {taskName: string; taskId: string; metrics: ChallengeMetric[]}[] {
    const objectives = cbProject.challengeBuilder?.objectives || [];
    const groups: {taskName: string; taskId: string; metrics: ChallengeMetric[]}[] = [];

    objectives.forEach(objective => {
      const tasks = objective.tasks || [];
      tasks.forEach(task => {
        if (!task.selected) return;

        // Deep clone metrics to avoid mutating the original data
        // Always merge with defaults so fields like targetSuccessThreshold are guaranteed
        const cycleLength = cbProject.challengeBuilder.cycleLength || 7;
        const metrics = (task.metrics || []).map(metric => {
          const defaults = this.createDefaultTarget(metric, task._id || '');
          const target = metric.target ? {...defaults, ...metric.target} : defaults;
          if (target.targetSuccessThreshold > cycleLength) {
            target.targetSuccessThreshold = cycleLength;
          }
          return {
            ...metric,
            target,
          };
        });

        groups.push({
          taskName: task.name,
          taskId: task._id || '',
          metrics,
        });
      });
    });

    return groups;
  }

  private createDefaultTarget(metric: ChallengeMetric, taskId: string): ChallengeTarget {
    return {
      targetName: metric.name,
      targetValue: null as unknown as number,
      targetOperator: 'gte',
      targetPeriod: 'perCycle',
      targetSuccessThreshold: 1,
      taskId,
      metricId: metric._id,
      targetMetric: {trackerId: '', metricId: ''},
    };
  }

  // Computed signal for template - just returns localTaskGroups
  taskTargetGroups = computed(() => this.localTaskGroups());

  // Operator options
  readonly operatorOptions: OperatorOption[] = [
    {display: 'or more', displayValue: 'or more', value: 'gte'},
    {display: 'or less', displayValue: 'or less', value: 'lte'},
    {display: 'equal to', displayValue: 'equal to', value: 'eq'},
  ];

  // Period options
  readonly periodOptions: PeriodOption[] = [
    {display: 'per cycle', value: 'perCycle'},
    {display: 'per day', value: 'perDay'},
  ];

  // Validation status
  isFormValid = computed(() => {
    const groups = this.taskTargetGroups();
    return groups.every(
      group =>
        group.metrics.length > 0 &&
        group.metrics.every(m => m.target?.targetValue != null && m.target?.targetValue !== ('' as unknown as number))
    );
  });

  // Effect to update validation state
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly validationEffect = effect(
    () => {
      const isValid = this.isFormValid();
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: isValid}));
    },
    {allowSignalWrites: true}
  );

  private initTaskIcons(cbProject: ChallengeBuilderProject): void {
    const objectives = cbProject.challengeBuilder?.objectives || [];
    const tasks = objectives.flatMap(obj => obj.tasks || []).filter(t => t.selected);
    const icons: Record<string, string> = {};

    for (const task of tasks) {
      if (!task._id) continue;
      if (task.icon) {
        icons[task._id] = task.icon;
      } else if (task.iconSuggestion) {
        const resolved = this.tasksService.findIconByKeyword(task.iconSuggestion);
        if (resolved) {
          icons[task._id] = resolved;
        }
      }
    }

    this.taskIcons.set(icons);
  }

  getTaskIcon(taskId: string): string {
    return this.taskIcons()[taskId] || '';
  }

  onTaskIconChange(taskId: string, icon: string): void {
    this.taskIcons.update(icons => ({...icons, [taskId]: icon}));
  }

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.nextClickSubscription?.unsubscribe();
      this.backClickSubscription?.unsubscribe();
    });
  }

  ngOnInit(): void {
    this.nextClickSubscription = this.nextClick$().subscribe(() => {
      this.onNextClick();
    });
    this.backClickSubscription = this.backClick$().subscribe(() => {
      this.onBackClick();
    });
  }

  getSuffixDisplay(metric: ChallengeMetric): string {
    if (metric.type === TrackerMetricTypes.Duration) {
      const normalizedType = this.normalizeDurationType(metric.typeOption);
      switch (normalizedType) {
        case 'hrs':
          return 'hours';
        case 'hrs/min':
          return 'hrs : min';
        case 'min':
          return 'minutes';
        case 'min/sec':
          return 'min : sec';
        case 'sec':
          return 'seconds';
        default:
          return '--';
      }
    }

    if (metric.type === TrackerMetricTypes.Numeric) {
      return metric.typeOption || '--';
    }

    if (metric.type === TrackerMetricTypes.Boolean) {
      return 'times';
    }

    if (metric.type === TrackerMetricTypes.Percentage) {
      return '%';
    }

    return '--';
  }

  isDualDurationInput(metric: ChallengeMetric): boolean {
    if (metric.type !== TrackerMetricTypes.Duration) return false;
    const normalizedType = this.normalizeDurationType(metric.typeOption);
    return normalizedType === 'hrs/min' || normalizedType === 'min/sec';
  }

  normalizeDurationType(typeOption: string | undefined): DurationType {
    if (!typeOption) return 'min';
    return DURATION_TYPE_MAP[typeOption] || 'min';
  }

  getOperatorDisplayValue(target: ChallengeTarget): string {
    const operator = this.operatorOptions.find(op => op.value === target.targetOperator);
    return operator?.displayValue || 'or more';
  }

  getPeriodDisplayValue(target: ChallengeTarget): string {
    const period = this.periodOptions.find(p => p.value === target.targetPeriod);
    return period?.display || 'per cycle';
  }

  getDaysOfCycleOptions(): number[] {
    const cycleLength = this.cbProject()?.challengeBuilder.cycleLength || 7;
    return Array.from({length: cycleLength}, (_, i) => i + 1);
  }

  getDaysOfCycleDisplayValue(target: ChallengeTarget): string {
    const cycleLength = this.cbProject()?.challengeBuilder.cycleLength || 7;
    const days = target.targetSuccessThreshold || 1;
    return `${days} of ${cycleLength} days`;
  }

  // Helper to update a metric's target
  private updateMetricTarget(taskId: string, metricIndex: number, updates: Partial<ChallengeTarget>): void {
    this.localTaskGroups.update(groups => {
      return groups.map(group => {
        if (group.taskId !== taskId) return group;
        return {
          ...group,
          metrics: group.metrics.map((metric, idx) => {
            if (idx !== metricIndex) return metric;
            return {
              ...metric,
              target: {...metric.target, ...updates},
            };
          }),
        };
      });
    });
  }

  onOperatorChange(taskId: string, metricIndex: number, operator: OperatorOption): void {
    this.updateMetricTarget(taskId, metricIndex, {targetOperator: operator.value});
  }

  onPeriodChange(taskId: string, metricIndex: number, period: PeriodOption): void {
    this.updateMetricTarget(taskId, metricIndex, {targetPeriod: period.value});
  }

  onDaysOfCycleChange(taskId: string, metricIndex: number, days: number): void {
    this.updateMetricTarget(taskId, metricIndex, {targetSuccessThreshold: days});
  }

  onValueChange(taskId: string, metricIndex: number, value: number | null): void {
    this.updateMetricTarget(taskId, metricIndex, {targetValue: value});
  }

  onInputChange(event: Event, taskId: string, metricIndex: number, metric: ChallengeMetric): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    let valueToStore: number | null = null;

    if (value !== '') {
      let numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        // For percentage type, clamp between 0 and 100
        if (metric.type === TrackerMetricTypes.Percentage) {
          numValue = Math.max(0, Math.min(100, numValue));
        }
        // For duration type with single input (hrs, min, or sec), convert to seconds
        if (metric.type === TrackerMetricTypes.Duration && metric.typeOption) {
          const normalizedType = this.normalizeDurationType(metric.typeOption);
          if (normalizedType === 'hrs') {
            valueToStore = Math.floor(numValue * 3600);
          } else if (normalizedType === 'min') {
            valueToStore = Math.floor(numValue * 60);
          } else if (normalizedType === 'sec') {
            valueToStore = Math.floor(numValue);
          } else {
            valueToStore = numValue;
          }
        } else {
          valueToStore = numValue;
        }
      }
    }

    this.onValueChange(taskId, metricIndex, valueToStore);
  }

  getDurationPrimaryValue(target: ChallengeTarget, durationType: DurationType): string {
    const value = target.targetValue;
    if (value == null || value === ('' as unknown as number)) return '';
    const totalSeconds = typeof value === 'string' ? parseInt(value, 10) : value;
    if (isNaN(totalSeconds)) return '';
    const display = secondsToDurationDisplay(totalSeconds, durationType);
    return display.primary > 0 ? String(display.primary) : '';
  }

  getDurationSecondaryValue(target: ChallengeTarget, durationType: DurationType): string {
    const value = target.targetValue;
    if (value == null || value === ('' as unknown as number)) return '';
    const totalSeconds = typeof value === 'string' ? parseInt(value, 10) : value;
    if (isNaN(totalSeconds)) return '';
    const display = secondsToDurationDisplay(totalSeconds, durationType);
    const isDualInput = durationType.includes('/');
    if (isDualInput && (display.primary > 0 || display.secondary > 0)) {
      return String(display.secondary).padStart(2, '0');
    }
    return display.secondary > 0 ? String(display.secondary) : '';
  }

  onDurationPrimaryChange(event: Event, taskId: string, metricIndex: number, metric: ChallengeMetric): void {
    const inputTarget = event.target as HTMLInputElement;
    const primaryValue = parseInt(inputTarget.value, 10) || 0;
    const durationType = this.normalizeDurationType(metric.typeOption);
    const currentSeconds = metric.target?.targetValue ?? 0;
    const currentDisplay = secondsToDurationDisplay(currentSeconds, durationType);
    const totalSeconds = durationDisplayToSeconds(primaryValue, currentDisplay.secondary, durationType);
    this.onValueChange(taskId, metricIndex, totalSeconds);
  }

  onDurationSecondaryChange(event: Event, taskId: string, metricIndex: number, metric: ChallengeMetric): void {
    const inputTarget = event.target as HTMLInputElement;
    const secondaryValue = parseInt(inputTarget.value, 10) || 0;
    const durationType = this.normalizeDurationType(metric.typeOption);
    const currentSeconds = metric.target?.targetValue ?? 0;
    const currentDisplay = secondsToDurationDisplay(currentSeconds, durationType);
    const totalSeconds = durationDisplayToSeconds(currentDisplay.primary, secondaryValue, durationType);
    this.onValueChange(taskId, metricIndex, totalSeconds);
  }

  onPercentageBlur(event: Event, taskId: string, metricIndex: number): void {
    const target = event.target as HTMLInputElement;
    let numValue = parseFloat(target.value);

    if (isNaN(numValue)) {
      numValue = 0;
    }

    // Clamp between 0 and 100
    numValue = Math.max(0, Math.min(100, numValue));
    target.value = String(numValue);

    this.onValueChange(taskId, metricIndex, numValue);
  }

  onDurationBlur(
    event: Event,
    taskId: string,
    metricIndex: number,
    metric: ChallengeMetric,
    position: 'primary' | 'secondary'
  ): void {
    const inputTarget = event.target as HTMLInputElement;
    let numValue = parseInt(inputTarget.value, 10);
    if (isNaN(numValue)) {
      numValue = 0;
    }

    const durationType = this.normalizeDurationType(metric.typeOption);
    const isDualInput = durationType.includes('/');
    if (isDualInput) {
      const parts = durationType.split('/');
      const unit = position === 'primary' ? parts[0] : parts[1];
      const max = unit === 'hrs' ? 999 : 59;
      numValue = Math.max(0, Math.min(numValue, max));
      const formattedValue = unit === 'min' || unit === 'sec' ? String(numValue).padStart(2, '0') : String(numValue);
      inputTarget.value = formattedValue;
    } else {
      numValue = Math.max(0, Math.min(numValue, 999));
      inputTarget.value = String(numValue);
    }

    const currentSeconds = metric.target?.targetValue ?? 0;
    const display = secondsToDurationDisplay(currentSeconds, durationType);
    const totalSeconds = durationDisplayToSeconds(display.primary, display.secondary, durationType);
    this.onValueChange(taskId, metricIndex, totalSeconds);
  }

  // Extract all targets from task groups for backend
  private extractAllTargets(): ChallengeTarget[] {
    const groups = this.localTaskGroups();
    const targets: ChallengeTarget[] = [];
    groups.forEach(group => {
      group.metrics.forEach(metric => {
        if (metric.target) {
          targets.push(metric.target);
        }
      });
    });
    return targets;
  }

  private onNextClick(): void {
    if (!this.isFormValid()) {
      this.messageService.add('Please complete all targets', 'error');
      this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
      return;
    }

    this.saveTargets(() => this.onNext.emit());
  }

  private onBackClick(): void {
    this.saveTargets(() => this.onBack.emit());
  }

  private saveTargets(onSuccess?: () => void): void {
    const allTargets = this.extractAllTargets();

    this.store$.dispatch(
      new ChallengeTargetsAction({
        projectId: this.cbProject()?._id || '',
        targets: allTargets,
        onSuccess: () => {
          if (this.destroyed) return;
          onSuccess?.();
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
        onFailure: (error: Error) => {
          if (this.destroyed) return;
          this.messageService.add('Failed to save targets', 'error');
          console.error('Failed to save targets:', error);
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
      })
    );
  }
}
